import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import ConfirmModal from '../components/ConfirmModal';
import AdminOrderDetails from '../components/AdminOrderDetails';
import { useNavigate } from 'react-router-dom';
import {
  fetchProducts, createProduct, updateProduct, uploadProductImage, deleteProductImage, deleteProduct,
  fetchPhotoboothPhotos, uploadPhotoboothPhoto, updatePhotoboothPhoto, deletePhotoboothPhoto,
  fetchBlogs, createBlog, updateBlog, deleteBlog,
  uploadBlogImage, deleteBlogImage,
  fetchUsers, updateUser, deleteUser,
  reorderProductImages, reorderProducts, reorderBlogs, reorderPhotoboothPhotos,
  fetchDropdownOptions, updateDropdownOptions,
  fetchNewsletterSubscribers, sendNewsletterToAll,
} from '../services/adminService';
import getOptimizedUrl from '../utils/imageUtils';
import { isAccessory } from '../utils/productUtils';
import { getAllOrders, updateOrderStatus, syncAllShippedOrders } from '../services/orderService';
import {
  fetchCoupons,
  createCoupon,
  updateCoupon,
  deleteCoupon,
} from '../services/couponService';
import { showErrorToast } from '../utils/errorHandler';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// --- Icons (SVG Components) ---
const Icons = {
  Box: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>,
  Users: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>,
  FileText: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>,
  ShoppingBag: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" /></svg>,
  Search: () => <img src="/search.svg" alt="Search" className="w-5 h-5 object-contain" />,
  Plus: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>,
  X: () => <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>,
  Upload: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>,
  Trash: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>,
  Logout: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>,
  Settings: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
  Ticket: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 14l6-6m-6 0h.01M15 14h.01M7 4h10a2 2 0 012 2v3a2 2 0 000 4v3a2 2 0 01-2 2H7a2 2 0 01-2-2v-3a2 2 0 000-4V6a2 2 0 012-2z" /></svg>
};

// --- Initial States ---
const emptyProduct = {
  category: '', product: '', print: '', color: '', fabricType: '', fabricPattern: '',
  fabricDescription: '', productType: '', storeCategory: '', length: '', lengthInInches: 0, sleeves: '',
  closure: '', productDescription: '', features: [], sizeAndFit: '', material: '',
  mrp: 0, discountedPrice: 0, totalQuantity: 0,
  sizeXXSQuantity: 0, sizeXSQuantity: 0, sizeSQuantity: 0, sizeMQuantity: 0,
  sizeLQuantity: 0, sizeXLQuantity: 0, sizeXXLQuantity: 0,
  slug: '', variantGroup: '', images: [], isShownInHomepage: false, isShownInFanout: false, fanoutPosition: '',
  metaTitle: '', metaDescription: '', seoKeywords: ''
};

const emptyBlog = { title: '', subtitle: '', slug: '', previewSnippet: '', excerpt: '', intro: '', readingTime: 5, content: [], images: [], tags: [] };
const emptyCoupon = {
  code: '',
  title: '',
  description: '',
  discountType: 'percentage',
  discountValue: 10,
  maxDiscountAmount: '',
  minOrderValue: '',
  validFrom: '',
  validUntil: '',
  usageLimit: '',
  perUserLimit: '',
  isActive: true,
  firstOrderOnly: false,
  nthOrderNumber: '',
  excludeSaleItems: false,
  allowCOD: true,
  allowRazorpay: true,
  allowedCollections: '',
  allowedProductTypes: '',
  allowedStoreCategories: '',
};

// --- Default Dropdown Options (Can be managed via Settings) ---
const defaultDropdownOptions = {
  collection: ['Roz Roz', 'Kuch Roz'],
  productType: ['Top', 'Skirt', 'Dress', 'Bags', 'Tie', 'Stole', 'Bandana', 'Pants', 'Kurti', 'Shirt', 'Shrug', 'Jacket', 'Shorts', 'Bottom'],
  storeCategories: ['Tops', 'Bottoms', 'Dresses', 'Jackets', 'Accessories'],
  color: ['Red', 'Blue', 'Yellow', 'Green', 'Black', 'Indigo', 'Brown', 'Teal', 'Off White', 'White', 'Brick Red', 'Orange', 'Pink', 'Multicolour'],
  fabricType: ['Modal', '60-60 Cambric Cotton', 'Mul Cotton', 'Cotton Blend Canderi Silk', 'Maheshwari Silk', 'Slub Cotton', 'cotton blend Linen', 'Cotton'],
  fabricPrint: ['Daboo (Mud Resist Technique)', 'Block Print', 'Bagru', 'Kalamkari', 'Black and White', 'Ajrak', 'Tie and Dye', 'Patch Work with Kantha', 'Ikat', 'Solid Dye'],
  fabricPattern: ['Bandhani', 'Floral', 'Plain', 'Motif', 'Stripes', 'All over', 'Animal', 'Lahriya'],
  length: ['Full', 'Knee', 'Maxi', 'Mini', 'Midi', 'Short', 'Crop', 'Midi', 'Standard'],
  sleeves: ['Sleeveless', 'Full Sleeves', '3/4th Sleeves', 'Half Sleeves', 'Puff Sleeves', 'Flared Sleeves', 'NA'],
  closure: ['Touch Buttons', 'Buttons', 'Hooks', 'String', 'Zip', 'Elastic']
};

const ORDER_STAGE_META = {
  ordered: { label: 'Ordered', badge: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
  being_made: { label: 'Being Made', badge: 'bg-orange-50 text-orange-700 border-orange-200' },
  shipped: { label: 'Shipped', badge: 'bg-blue-50 text-blue-700 border-blue-200' },
  delivered: { label: 'Delivered', badge: 'bg-green-50 text-green-700 border-green-200' },
  cancelled: { label: 'Cancelled', badge: 'bg-red-50 text-red-700 border-red-200' },
  return_requested: { label: 'Return Requested', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  return_approved: { label: 'Return Approved', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  return_processing: { label: 'Return In Transit', badge: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  return_received_to_store: { label: 'Return Received', badge: 'bg-slate-50 text-slate-700 border-slate-200' },
  refund_initiated: { label: 'Refund Initiated', badge: 'bg-purple-50 text-purple-700 border-purple-200' },
};

const getOrderStageMeta = (stage) => ORDER_STAGE_META[stage] || {
  label: String(stage || 'ordered').replace(/_/g, ' '),
  badge: 'bg-gray-50 text-gray-700 border-gray-200',
};

const getOrderStatusLabel = (item) => {
  if (item?.orderStage === 'cancelled' && (item?.cancelledBy === 'user' || item?.cancelledBy === 'customer')) {
    return 'Cancelled by customer';
  }
  return getOrderStageMeta(item?.orderStage).label;
};

const shouldShowRefundInitiate = (item) => (
  item?.orderStage === 'cancelled' &&
  (item?.cancelledBy === 'user' || item?.cancelledBy === 'customer') &&
  (item?.paymentMethod === 'Razorpay' || item?.paymentResult?.razorpay_payment_id)
);

const getOrderActivitySummary = (order) => {
  const items = order?.items || [];
  const cancelledCount = items.filter((item) => item.cancelledStatus === 'cancelled').length;
  const returnCount = items.filter((item) => item.returnStatus && item.returnStatus !== 'none' && item.returnStatus !== 'rejected').length;
  const exchangeCount = items.filter((item) => item.exchangeStatus && item.exchangeStatus !== 'none' && item.exchangeStatus !== 'rejected').length;
  const refundCount = items.filter((item) => item.refundDetails?.refundStatus && item.refundDetails.refundStatus !== 'none').length;

  return {
    cancelledCount,
    returnCount,
    exchangeCount,
    refundCount,
    labels: [
      cancelledCount > 0 ? `${cancelledCount} cancelled` : null,
      returnCount > 0 ? `${returnCount} returned` : null,
      exchangeCount > 0 ? `${exchangeCount} exchanged` : null,
      refundCount > 0 ? `${refundCount} refunded` : null,
    ].filter(Boolean),
  };
};

const getOrderCategory = (order) => {
  const stage = order?.orderStage || 'ordered';
  const summary = getOrderActivitySummary(order);

  if (stage === 'cancelled' || summary.cancelledCount > 0) return 'cancelled';
  if (summary.returnCount > 0) return 'returned';
  if (summary.exchangeCount > 0) return 'replaced';
  if (stage === 'delivered') return 'delivered';
  if (stage === 'being_made' || stage === 'shipped') return 'processing';
  return 'new';
};

const ORDER_CATEGORY_META = {
  all: { label: 'All Orders', badge: 'bg-gray-100 text-gray-700 border-gray-200' },
  new: { label: 'New', badge: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
  processing: { label: 'Processing', badge: 'bg-blue-50 text-blue-700 border-blue-200' },
  delivered: { label: 'Delivered', badge: 'bg-green-50 text-green-700 border-green-200' },
  returned: { label: 'Returned', badge: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  replaced: { label: 'Replaced', badge: 'bg-purple-50 text-purple-700 border-purple-200' },
  cancelled: { label: 'Cancelled', badge: 'bg-red-50 text-red-700 border-red-200' },
};

// --- STABLE EXTERNAL COMPONENTS (Prevents focus loss) ---

const InputGroup = ({ label, name, type = "text", placeholder, colSpan = 1, formData, onChange, rows }) => {
  const value = formData && formData[name] !== undefined && formData[name] !== null ? formData[name] : '';
  const checked = Boolean(formData && formData[name]);
  return (
    <div className={colSpan === 2 ? "col-span-2" : ""}>
      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">{label}</label>
      {type === 'textarea' ? (
        <textarea
          name={name} value={value} onChange={onChange} placeholder={placeholder} rows={rows || 3}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-shadow"
        />
      ) : type === 'checkbox' ? (
        <label className="flex items-center mt-2 cursor-pointer">
          <input
            type="checkbox" name={name} checked={checked} onChange={onChange}
            className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
          />
          <span className="ml-3 text-sm font-medium text-gray-700">{placeholder || label}</span>
        </label>
      ) : (
        <input
          type={type} name={name} value={value} onChange={onChange} placeholder={placeholder}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-shadow"
        />
      )}
    </div>
  );
};

const SelectGroup = ({ label, name, options, colSpan = 1, formData, onChange }) => {
  const value = formData && formData[name] !== undefined && formData[name] !== null ? formData[name] : '';
  return (
    <div className={colSpan === 2 ? "col-span-2" : ""}>
      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">{label}</label>
      <select
        name={name}
        value={value}
        onChange={onChange}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-shadow bg-white cursor-pointer"
      >
        <option value="">Select {label}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </div>
  );
};

// Sortable Product Row Component
const SortableProductRow = ({ item, handleOpenModal, handleDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Calculate actual stock from individual sizes or freesize
  const calculateStock = () => {
    if (isAccessory(item.productType)) {
      return item.freesizeQuantity || 0;
    }
    return (
      (item.sizeXXSQuantity || 0) +
      (item.sizeXSQuantity || 0) +
      (item.sizeSQuantity || 0) +
      (item.sizeMQuantity || 0) +
      (item.sizeLQuantity || 0) +
      (item.sizeXLQuantity || 0) +
      (item.sizeXXLQuantity || 0)
    );
  };

  const actualStock = calculateStock();

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className="hover:bg-gray-50/80 transition-colors group"
    >
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          {/* Drag Handle */}
          <div
            {...attributes}
            {...listeners}
            title="Drag to reorder"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8h16M4 16h16" />
            </svg>
          </div>
          <div className="flex-shrink-0 h-12 w-12 bg-white rounded-lg overflow-hidden border border-gray-200">
            {item.images && item.images[0] ? (
              <img className="h-full w-full object-cover bg-white" src={getOptimizedUrl(item.images[0] || '', 800)} alt="" />
            ) : <div className="h-full w-full flex items-center justify-center text-gray-300"><Icons.Box /></div>}
          </div>
          <div className="ml-4">
            <div className="text-sm font-bold text-gray-900">{typeof item.product === 'string' ? item.product : (item.product?.product || '')}</div>
            <div className="text-xs text-gray-500">{item.productType}</div>
            {item.variantGroup && (
              <div className="text-xs text-indigo-600 mt-1">Group: {item.variantGroup}</div>
            )}
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-gray-700">Collection ({item.category})</div>
        <div className="text-xs text-gray-400">Color ({item.color}) • Fabric Type ({item.fabricType})</div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm font-semibold text-gray-900">₹{item.discountedPrice || item.mrp}</div>
        <div className="text-xs text-gray-500">
          Stock: <span className={actualStock < 10 ? "text-red-500 font-bold" : actualStock === 0 ? "text-gray-400" : "text-green-600"}>
            {actualStock}
          </span>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <button 
          onClick={() => handleOpenModal(item)} 
          className="text-indigo-600 bg-white hover:bg-indigo-200 p-2 rounded-lg mr-2 transition-colors"
          title="Edit"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
        </button>
        <button 
          onClick={() => handleDelete(item._id)} 
          className="text-red-600 bg-white hover:bg-red-100 p-2 rounded-lg transition-colors"
          title="Delete"
        >
          <Icons.Trash />
        </button>
      </td>
    </tr>
  );
};

const SortableBlogRow = ({ item, handleOpenModal, handleDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item._id });

  return (
    <tr
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
      className="hover:bg-gray-50/80 transition-colors group"
    >
      <td className="px-6 py-4">
        <div className="flex items-center gap-3">
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 transition-colors"
            title="Drag to reorder"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8h16M4 16h16" />
            </svg>
          </div>
          {item.images && item.images[0] && (
            <img src={getOptimizedUrl(item.images[0] || '', 200)} alt="" className="w-10 h-10 rounded object-cover border border-gray-200" />
          )}
          <div>
            <div className="text-sm font-medium text-gray-900">{item.title}</div>
            <div className="text-xs text-gray-400 line-clamp-1">{item.previewSnippet || item.intro || item.excerpt}</div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 text-xs font-mono text-gray-500 bg-gray-50 rounded p-1 w-fit">{item.slug}</td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <button
          onClick={() => handleOpenModal(item)}
          className="text-indigo-600 bg-white hover:bg-indigo-200 p-2 rounded-lg mr-2 transition-colors"
          title="Edit"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
        </button>
        <button
          onClick={() => handleDelete(item._id)}
          className="text-red-600 bg-white hover:bg-red-100 p-2 rounded-lg transition-colors"
          title="Delete"
        >
          <Icons.Trash />
        </button>
      </td>
    </tr>
  );
};

const SortablePhotoboothRow = ({ item, handleOpenModal, handleDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item._id });

  return (
    <tr
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
      className="hover:bg-gray-50/80 transition-colors group"
    >
      <td className="px-6 py-4">
        <div className="flex items-center gap-3">
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 transition-colors"
            title="Drag to reorder"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8h16M4 16h16" />
            </svg>
          </div>
          {item.imageUrl ? (
            <img src={getOptimizedUrl(item.imageUrl || '', 200)} alt="" className="w-12 h-12 rounded object-cover border border-gray-200" />
          ) : (
            <div className="w-12 h-12 bg-gray-50 rounded flex items-center justify-center text-gray-300">No</div>
          )}
        </div>
      </td>
      <td className="px-6 py-4">
        <div className="text-sm font-medium text-gray-900">{item.garmentName || '—'}</div>
        <div className="text-xs text-gray-400">{item.alt || ''}</div>
      </td>
      <td className="px-6 py-4 text-sm text-gray-700">{item.product?.product || '-'}</td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <button
          onClick={() => handleOpenModal(item)}
          className="text-indigo-600 bg-white hover:bg-indigo-200 p-2 rounded-lg mr-2 transition-colors"
          title="Edit"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
        </button>
        <button
          onClick={() => handleDelete(item._id)}
          className="text-red-600 bg-white hover:bg-red-100 p-2 rounded-lg transition-colors"
          title="Delete"
        >
          <Icons.Trash />
        </button>
      </td>
    </tr>
  );
};

const TabButton = ({ id, label, icon, activeTab, setActiveTab, setSearchQuery }) => (
  <button
    onClick={() => { setActiveTab(id); setSearchQuery(''); }}
    className={`w-full flex items-center space-x-3 px-6 py-3.5 transition-all duration-200 border-r-4
      ${activeTab === id 
        ? 'bg-indigo-50 border-indigo-600 text-indigo-700' 
        : 'border-transparent text-gray-500 hover:bg-gray-50 hover:text-gray-900'}`}
  >
    {icon}
    <span className="font-medium tracking-wide text-sm">{label}</span>
  </button>
);

// Sortable Image Item Component
const SortableImageItem = ({ img, index, onDeleteImage }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: img });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="relative group w-24 h-24 cursor-move"
    >
      <img
        src={img}
        alt="preview"
        className="w-full h-full object-cover rounded-lg shadow-sm border border-gray-200"
      />
      <div className="absolute top-1 left-1 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded">
        {index + 1}
      </div>
      <button
        type="button"
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          onDeleteImage(img);
        }}
        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-md hover:bg-black opacity-0 group-hover:opacity-100 transition-all transform scale-90 group-hover:scale-100 z-10"
      >
        <Icons.X />
      </button>
    </div>
  );
};

const ImageUploadSection = ({ images, fileMap, setFileMap, onDeleteImage, onReorderImages }) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = images.indexOf(active.id);
      const newIndex = images.indexOf(over.id);
      const newImages = arrayMove(images, oldIndex, newIndex);
      onReorderImages(newImages);
    }
  };

  return (
    <div className="col-span-2 space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Gallery
          {images && images.length > 0 && (
            <span className="text-xs text-gray-500 ml-2">
              (Drag to reorder - Order: 1, 2, 3...)
            </span>
          )}
        </label>
        {images && images.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={images} strategy={rectSortingStrategy}>
              <div className="flex flex-wrap gap-4 mt-3">
                {images.map((img, i) => (
                  <SortableImageItem
                    key={img}
                    img={img}
                    index={i}
                    onDeleteImage={onDeleteImage}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <div className="w-24 h-24 bg-gray-50 rounded-lg border border-dashed border-gray-300 flex items-center justify-center text-xs text-gray-400 mt-3">
            No Images
          </div>
        )}
      </div>
      <div className="border-t border-gray-100 pt-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Upload New
        </label>
        <label className="flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 transition-colors group">
          <div className="space-y-1 text-center">
            <div className="text-gray-400 group-hover:text-black transition-colors">
              <Icons.Upload />
            </div>
            <div className="flex text-sm text-gray-600">
              <span className="font-medium text-indigo-600 group-hover:text-black">
                Click to upload
              </span>
              <p className="pl-1">or drag and drop</p>
            </div>
            <p className="text-xs text-gray-500">
              {fileMap ? fileMap.name : 'PNG, JPG up to 5MB'}
            </p>
          </div>
          <input
            type="file"
            className="sr-only"
            onChange={(e) => setFileMap(e.target.files[0])}
          />
        </label>
      </div>
    </div>
  );
};

// --- Settings Panel Component ---
const SettingsPanel = ({ dropdownOptions, setDropdownOptions }) => {
  const [editingField, setEditingField] = useState(null);
  const [newOption, setNewOption] = useState('');
  const [saving, setSaving] = useState(false);
  const [subscribers, setSubscribers] = useState([]);
  const [newsletterSubject, setNewsletterSubject] = useState('');
  const [newsletterMessage, setNewsletterMessage] = useState('');
  const [sendingNewsletter, setSendingNewsletter] = useState(false);
  const token = localStorage.getItem('token') || localStorage.getItem('adminToken');

  const fieldLabels = {
    collection: 'Collection',
    productType: 'Product Type',
    storeCategories: 'Store Categories',
    color: 'Color',
    fabricType: 'Fabric Type',
    fabricPrint: 'Fabric Print',
    fabricPattern: 'Fabric Pattern',
    length: 'Length',
    sleeves: 'Sleeves',
    closure: 'Closure'
  };

  const saveToBackend = async (updated) => {
    setSaving(true);
    try {
      await updateDropdownOptions(updated, token);
      setDropdownOptions(updated);
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to save to database');
    } finally {
      setSaving(false);
    }
  };

  const handleAddOption = async (field) => {
    if (!newOption.trim()) return;
    
    const updated = {
      ...dropdownOptions,
      [field]: [...dropdownOptions[field], newOption.trim()]
    };
    
    await saveToBackend(updated);
    setNewOption('');
    setEditingField(null);
    toast.success(`Added "${newOption}" to ${fieldLabels[field]}`);
  };

  const handleRemoveOption = async (field, option) => {
    const updated = {
      ...dropdownOptions,
      [field]: dropdownOptions[field].filter(opt => opt !== option)
    };
    
    await saveToBackend(updated);
    toast.success(`Removed "${option}" from ${fieldLabels[field]}`);
  };

  const handleResetToDefaults = async () => {
    await saveToBackend(defaultDropdownOptions);
    toast.success('Reset to default options');
  };

  useEffect(() => {
    const loadSubscribers = async () => {
      try {
        const res = await fetchNewsletterSubscribers(token);
        setSubscribers(Array.isArray(res?.subscribers) ? res.subscribers : []);
      } catch (error) {
        console.error('Failed to fetch newsletter subscribers:', error);
      }
    };
    if (token) loadSubscribers();
  }, [token]);

  const handleSendNewsletter = async () => {
    const subject = newsletterSubject.trim();
    const message = newsletterMessage.trim();
    if (!subject || !message) {
      toast.error('Please add both subject and message');
      return;
    }
    if (subscribers.length === 0) {
      toast.error('No newsletter subscribers found');
      return;
    }

    setSendingNewsletter(true);
    try {
      const res = await sendNewsletterToAll({ subject, message }, token);
      toast.success(res?.message || 'Newsletter sent successfully');
      setNewsletterSubject('');
      setNewsletterMessage('');
    } catch (error) {
      console.error('Failed to send newsletter:', error);
      showErrorToast(error, 'Failed to send newsletter');
    } finally {
      setSendingNewsletter(false);
    }
  };

  return (
    <div className="space-y-6">
      {saving && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-center gap-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          <span className="text-sm text-blue-800 font-medium">Saving to database...</span>
        </div>
      )}
      
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Dropdown Options Management</h3>
            <p className="text-sm text-gray-500 mt-1">Manage dropdown options for product fields. Changes sync across all devices.</p>
          </div>
          <button
            onClick={handleResetToDefaults}
            disabled={saving}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          >
            Reset to Defaults
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {Object.keys(dropdownOptions).filter(f => f !== 'categoryMapping').map((field) => (
            <div key={field} className="border border-gray-200 rounded-xl p-4 bg-gray-50">
              <div className="flex justify-between items-center mb-3">
                <h4 className="font-bold text-gray-800 text-sm uppercase tracking-wide">{fieldLabels[field]}</h4>
                <button
                  onClick={() => setEditingField(editingField === field ? null : field)}
                  className="text-indigo-600 hover:text-indigo-800 text-xs font-medium"
                >
                  {editingField === field ? 'Cancel' : '+ Add New'}
                </button>
              </div>

              {editingField === field && (
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={newOption}
                    onChange={(e) => setNewOption(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddOption(field)}
                    placeholder={`New ${fieldLabels[field]}...`}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    autoFocus
                  />
                  <button
                    onClick={() => handleAddOption(field)}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
                  >
                    Add
                  </button>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                {dropdownOptions[field].map((option, idx) => (
                  <div
                    key={idx}
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm group hover:border-red-300 transition-colors"
                  >
                    <span className="text-gray-700">{option}</span>
                    <button
                      onClick={() => handleRemoveOption(field, option)}
                      className="text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Remove"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Newsletter Management</h3>
            <p className="text-sm text-gray-500 mt-1">
              Stored subscribers: <span className="font-semibold text-gray-800">{subscribers.length}</span>
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Subject</label>
            <input
              type="text"
              value={newsletterSubject}
              onChange={(e) => setNewsletterSubject(e.target.value)}
              placeholder="Enter newsletter subject"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-shadow"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Message</label>
            <textarea
              value={newsletterMessage}
              onChange={(e) => setNewsletterMessage(e.target.value)}
              rows={6}
              placeholder="Write your newsletter message here..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-shadow"
            />
          </div>

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-gray-500">This sends to all active newsletter subscribers.</p>
            <button
              onClick={handleSendNewsletter}
              disabled={sendingNewsletter || subscribers.length === 0}
              className="px-5 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-black shadow-lg transition-all active:scale-95 font-medium disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {sendingNewsletter ? 'Sending...' : `Send to ${subscribers.length} subscribers`}
            </button>
          </div>
        </div>
      </div>

      {/* Category Mapping Section */}

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="flex-1">
            <h4 className="font-bold text-blue-900 text-sm mb-1">How it works</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Add new options by clicking "+ Add New" and typing the option name</li>
              <li>• Remove options by hovering over them and clicking the × button</li>
              <li>• Changes are saved automatically to the database</li>
              <li>• These options sync across all devices and admins</li>
              <li>• Options appear in product add/edit forms immediately</li>
              <li>• <strong>Store Categories</strong> controls the category options available on each product (e.g. Tops, Bottoms)</li>
              <li>• Click "Reset to Defaults" to restore original options</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- MAIN COMPONENT ---

export default function Admin() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token') || localStorage.getItem('adminToken');
  const storedUser = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null;
  const isAdmin = !!(storedUser && storedUser.role === 'admin');

  // --- State ---
  const [activeTab, setActiveTab] = useState('products');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [orderCategoryFilter, setOrderCategoryFilter] = useState('all');
  
  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create'); 
  const [formData, setFormData] = useState({});
  const [fileMap, setFileMap] = useState(null); 
  const [productsList, setProductsList] = useState([]);
  const [productFormTab, setProductFormTab] = useState('basic'); 

  // Order Details Modal State
  const [isOrderDetailsOpen, setIsOrderDetailsOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [selectedOrderInitialTab, setSelectedOrderInitialTab] = useState('overview');

  // Confirm Modal State - MOVED TO TOP to avoid Reference Errors
  const [confirm, setConfirm] = useState({ open: false, title: '', description: '', onConfirm: () => {}, onCancel: () => {} });

  // Dropdown Options State (managed in Settings - now from backend)
  const [dropdownOptions, setDropdownOptions] = useState(defaultDropdownOptions);
  const [couponForm, setCouponForm] = useState({ ...emptyCoupon });
  const [editingCouponId, setEditingCouponId] = useState(null);

  // --- Effects ---
  useEffect(() => {
    if (!token || !isAdmin) navigate('/');
    else {
      setData([]);
      fetchData();
    }
    // eslint-disable-next-line
  }, [activeTab]);

  // Fetch dropdown options on mount
  useEffect(() => {
    const loadDropdownOptions = async () => {
      try {
        const options = await fetchDropdownOptions(token);
        setDropdownOptions(options);
      } catch (error) {
        console.error('Failed to fetch dropdown options:', error);
        toast.error('Failed to load dropdown options');
      }
    };

    if (token && isAdmin) {
      loadDropdownOptions();
    }
  }, [token, isAdmin]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let res = [];
      if (activeTab === 'products') res = await fetchProducts(token);
      else if (activeTab === 'photobooth') res = await fetchPhotoboothPhotos(token);
      else if (activeTab === 'blogs') res = await fetchBlogs(token);
      else if (activeTab === 'users') res = await fetchUsers(token);
      else if (activeTab === 'orders') res = await getAllOrders(token);
      else if (activeTab === 'coupons') res = await fetchCoupons();
      setData(res);
    } catch (err) {
      console.error("Failed to fetch data", err);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const refreshOrdersList = async ({ showSuccessToast = true } = {}) => {
    if (activeTab !== 'orders') return;

    setLoading(true);
    try {
      const orders = await getAllOrders(token);
      setData(orders);
      if (showSuccessToast) toast.success('Orders list refreshed');
    } catch (err) {
      console.error('Failed to refresh orders list', err);
      toast.error('Failed to refresh orders list');
    } finally {
      setLoading(false);
    }
  };

  // --- Handlers ---
  const handleOpenModal = (item = null, initialTab = 'overview') => {
    // For orders, use the order details modal instead
    if (activeTab === 'orders') {
      if (item) {
        setSelectedOrderId(item._id);
        setSelectedOrderInitialTab(initialTab);
        setIsOrderDetailsOpen(true);
      }
      return;
    }

    setFileMap(null);
    setProductFormTab('basic'); 
      if (item) {
      setModalMode('edit');
      const prep = { ...item };
      // Ensure `intro` is available when editing blogs (fall back to excerpt)
      if (activeTab === 'blogs') {
        prep.intro = prep.intro || prep.excerpt || '';
        prep.previewSnippet = prep.previewSnippet || prep.intro || prep.excerpt || '';
        prep.readingTime = prep.readingTime !== undefined ? prep.readingTime : 5;
        // Normalize content into an array for the admin editor
        if (Array.isArray(prep.content)) {
          // keep as-is
        } else if (typeof prep.content === 'string' && prep.content.trim()) {
          prep.content = prep.content.split(/\n\s*\n/).map(s => s.trim()).filter(Boolean);
        } else {
          prep.content = [];
        }
      }
      if (activeTab === 'products' && Array.isArray(prep.features)) {
        prep.features = prep.features.join(', ');
      }
      if (activeTab === 'products') {
        prep.isShownInFanout = !!prep.isShownInFanout;
        prep.fanoutPosition = prep.fanoutPosition || '';
      }
      if (activeTab === 'photobooth') {
        // normalize product to id for select input
        prep.product = prep.product && prep.product._id ? prep.product._id : prep.product;
        prep.modelName = prep.modelName || '';
        // load products list for select
        fetchProducts(token).then(list => setProductsList(list)).catch(() => {});
      }
      setFormData(prep);
    } else {
      setModalMode('create');
      if (activeTab === 'photobooth') setFormData({ garmentName: '', product: '', imageUrl: '', alt: '', modelName: '' });
      if (activeTab === 'products') setFormData({ ...emptyProduct });
      if (activeTab === 'blogs') setFormData({ ...emptyBlog });
    }
    if (activeTab === 'photobooth') {
      fetchProducts(token).then(list => setProductsList(list)).catch(() => {});
    }
    setIsModalOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const updated = { ...prev, [name]: newValue };

      // Auto-set sizes to ["Freesize"] when productType is an accessory type
      if (name === 'productType' && isAccessory(newValue)) {
        updated.sizes = ['Freesize'];
      }

      // If fanout is disabled, clear slot assignment.
      if (name === 'isShownInFanout' && !newValue) {
        updated.fanoutPosition = '';
      }

      return updated;
    });
  };

  const handleDeleteImage = async (imgUrl) => {
    setConfirm({
      open: true,
      title: 'Delete image?',
      description: 'Are you sure you want to delete this image?',
      onConfirm: async () => {
        try {
          if (activeTab === 'products') await deleteProductImage(formData._id, imgUrl, token);
          else if (activeTab === 'blogs') await deleteBlogImage(formData._id, imgUrl, token);
          
          setFormData(prev => ({ ...prev, images: (prev.images || []).filter(x => x !== imgUrl) }));
          toast.success('Image deleted');
        } catch (e) { toast.error('Could not delete image'); }
        finally { setConfirm(c => ({ ...c, open: false })); }
      },
      onCancel: () => setConfirm(c => ({ ...c, open: false }))
    });
  };

  const handleReorderImages = async (newImages) => {
    // Update local state immediately for smooth UX
    setFormData(prev => ({ ...prev, images: newImages }));
    
    // If editing an existing product, save to backend
    if (modalMode === 'edit' && formData._id) {
      try {
        if (activeTab === 'products') {
          await reorderProductImages(formData._id, newImages, token);
          toast.success('Image order saved');
        }
        // Note: You can add similar logic for blogs if needed
      } catch (e) {
        console.error(e);
        showErrorToast(e, 'Failed to save image order');
      }
    }
  };

  const handleUserRoleToggle = async (user) => {
    const newRole = user.role === 'admin' ? 'user' : 'admin';
    const action = newRole === 'admin' ? 'promote to Admin' : 'demote to User';
    
    setConfirm({
      open: true,
      title: `Confirm ${action}`,
      description: `Are you sure you want to ${action} for ${user.name}?`,
      onConfirm: async () => {
        try {
          await updateUser(user._id, { role: newRole }, token);
          fetchData();
          toast.success('Role updated');
        } catch (err) {
          showErrorToast(err, 'Failed to update role');
        } finally { setConfirm(c => ({ ...c, open: false })); }
      },
      onCancel: () => setConfirm(c => ({ ...c, open: false }))
    });
  };

  const handleChangeOrderStatus = async (orderId, newStage) => {
    setLoading(true);
    try {
      await updateOrderStatus(orderId, newStage);
      fetchData();
      toast.success(`Order moved to ${getOrderStageMeta(newStage).label}`);
    } catch (err) {
      console.error('Failed to update order stage', err);
      showErrorToast(err, 'Failed to update order stage');
    } finally {
      setLoading(false);
    }
  };

  const handleProductDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = filteredData.findIndex(item => item._id === active.id);
    const newIndex = filteredData.findIndex(item => item._id === over.id);

    // Update local state immediately for smooth UX
    const newData = arrayMove(filteredData, oldIndex, newIndex);
    setData(newData);

    // Prepare the product orders for backend update
    const productOrders = newData.map((product, index) => ({
      id: product._id,
      sortOrder: index,
    }));

    try {
      await reorderProducts(productOrders, token);
      toast.success('Product order updated');
    } catch (error) {
      console.error('Failed to reorder products:', error);
      toast.error('Failed to save product order');
      // Revert on error
      fetchData();
    }
  };

  const handleBlogDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = data.findIndex((item) => item._id === active.id);
    const newIndex = data.findIndex((item) => item._id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const newData = arrayMove(data, oldIndex, newIndex);
    setData(newData);

    const blogOrders = newData.map((blog, index) => ({ id: blog._id, sortOrder: index }));
    try {
      await reorderBlogs(blogOrders, token);
      toast.success('Blog order updated');
    } catch (error) {
      console.error('Failed to reorder blogs:', error);
      toast.error('Failed to save blog order');
      fetchData();
    }
  };

  const handlePhotoboothDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = data.findIndex((item) => item._id === active.id);
    const newIndex = data.findIndex((item) => item._id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const newData = arrayMove(data, oldIndex, newIndex);
    setData(newData);

    const photoOrders = newData.map((photo, index) => ({ id: photo._id, sortOrder: index }));
    try {
      await reorderPhotoboothPhotos(photoOrders, token);
      toast.success('Photobooth order updated');
    } catch (error) {
      console.error('Failed to reorder photobooth items:', error);
      toast.error('Failed to save photobooth order');
      fetchData();
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px of movement before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let payload = { ...formData };
      
      // Feature formatting
      if (activeTab === 'products' && typeof payload.features === 'string') {
        payload.features = payload.features.split(',').map(s => s.trim()).filter(Boolean);
      }

      let result;
      // Map intro -> excerpt for blogs so backend receives excerpt
      if (activeTab === 'blogs') {
        if (payload.intro !== undefined) payload.excerpt = payload.intro;
        payload.previewSnippet = String(payload.previewSnippet || '').trim();
        // Convert textarea content into array blocks separated by blank lines
        if (typeof payload.content === 'string') {
          payload.content = payload.content.split(/\n\s*\n/).map(s => s.trim()).filter(Boolean);
        }
        // Ensure readingTime is a number (minutes)
        if (payload.readingTime !== undefined) payload.readingTime = Number(payload.readingTime) || 5;
      }

      if (activeTab === 'products') {
        payload.isShownInFanout = !!payload.isShownInFanout;
        payload.fanoutPosition = payload.isShownInFanout ? (payload.fanoutPosition || '') : '';

        if (payload.isShownInFanout) {
          if (!['left', 'center', 'right'].includes(payload.fanoutPosition)) {
            throw new Error('Please select Fanout Alignment (Left, Center, or Right)');
          }

          const otherFanoutProducts = (data || []).filter(
            (item) => item.isShownInFanout && item._id !== payload._id
          );

          if (otherFanoutProducts.length >= 3) {
            throw new Error('Only 3 products can be selected for fanout');
          }

          const slotTaken = otherFanoutProducts.some(
            (item) => item.fanoutPosition === payload.fanoutPosition
          );

          if (slotTaken) {
            throw new Error(`Fanout alignment "${payload.fanoutPosition}" is already used`);
          }
        }

        if (modalMode === 'edit') result = await updateProduct(payload._id, payload, token);
        else result = await createProduct(payload, token);
        
        if (fileMap) await uploadProductImage(result._id, fileMap, token);
      
      } else if (activeTab === 'photobooth') {
        if (modalMode === 'create') {
          if (!fileMap) throw new Error('Please select an image to upload');
          result = await uploadPhotoboothPhoto(fileMap, payload.garmentName, payload.product, payload.modelName, payload.alt, token);
        } else {
          // If admin selected a new file while editing, include it so backend replaces the image
          if (fileMap) {
            result = await updatePhotoboothPhoto(payload._id, { image: fileMap, garmentName: payload.garmentName, product: payload.product, alt: payload.alt, modelName: payload.modelName }, token);
          } else {
            result = await updatePhotoboothPhoto(payload._id, { garmentName: payload.garmentName, product: payload.product, alt: payload.alt, modelName: payload.modelName }, token);
          }
        }

      } else if (activeTab === 'blogs') {
        if (modalMode === 'edit') result = await updateBlog(payload._id, payload, token);
        else result = await createBlog(payload, token);

        if (fileMap && result._id) {
          try { await uploadBlogImage(result._id, fileMap, token); } catch (e) { console.error('Blog image upload failed', e); }
        }
      } else if (activeTab === 'orders') {
        try {
          await updateOrderStatus(payload._id, payload.orderStage);
          toast.success('Order updated');
        } catch (err) {
          throw err;
        }
      }

      setIsModalOpen(false);
      // Clear file selection and refresh data
      setFileMap(null);
      fetchData();
    } catch (err) {
      toast.error("Error saving: " + (err.message || ''));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setConfirm({
      open: true,
      title: 'Confirm Delete',
      description: 'Are you sure you want to delete this? This cannot be undone.',
      onConfirm: async () => {
        try {
          if (activeTab === 'products') await deleteProduct(id, token);
          else if (activeTab === 'photobooth') await deletePhotoboothPhoto(id, token);
          else if (activeTab === 'blogs') await deleteBlog(id, token);
          else if (activeTab === 'users') await deleteUser(id, token);
          fetchData();
          toast.success('Deleted successfully');
        } catch (err) {
          toast.error('Error deleting: ' + (err.message || ''));
        } finally { setConfirm(c => ({ ...c, open: false })); }
      },
      onCancel: () => setConfirm(c => ({ ...c, open: false }))
    });
  };

  const handleCouponFieldChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === 'checkbox' ? checked : value;

    setCouponForm((prev) => {
      const next = {
        ...prev,
        [name]: nextValue,
      };

      // Keep first-order behavior intact by clearing nth-order when first-order-only is enabled.
      if (name === 'firstOrderOnly' && checked) {
        next.nthOrderNumber = '';
      }

      // If nth-order is set, automatically disable first-order-only to avoid conflicting rules.
      if (name === 'nthOrderNumber' && String(value || '').trim() !== '') {
        next.firstOrderOnly = false;
      }

      return next;
    });
  };

  const resetCouponForm = () => {
    setCouponForm({ ...emptyCoupon });
    setEditingCouponId(null);
  };

  const mapCouponForForm = (coupon) => ({
    code: coupon.code || '',
    title: coupon.title || '',
    description: coupon.description || '',
    discountType: coupon.discountType || 'percentage',
    discountValue: coupon.discountValue ?? 0,
    maxDiscountAmount: coupon.maxDiscountAmount ?? '',
    minOrderValue: coupon.minOrderValue ?? '',
    validFrom: coupon.validFrom ? new Date(coupon.validFrom).toISOString().slice(0, 16) : '',
    validUntil: coupon.validUntil ? new Date(coupon.validUntil).toISOString().slice(0, 16) : '',
    usageLimit: coupon.usageLimit ?? '',
    perUserLimit: coupon.perUserLimit ?? '',
    isActive: !!coupon.isActive,
    firstOrderOnly: !!coupon.firstOrderOnly,
    nthOrderNumber: coupon.nthOrderNumber ?? '',
    excludeSaleItems: !!coupon.excludeSaleItems,
    allowCOD: !(coupon.allowedPaymentMethods || []).length || (coupon.allowedPaymentMethods || []).includes('COD'),
    allowRazorpay: !(coupon.allowedPaymentMethods || []).length || (coupon.allowedPaymentMethods || []).includes('Razorpay'),
    allowedCollections: (coupon.allowedCollections || []).join(', '),
    allowedProductTypes: (coupon.allowedProductTypes || []).join(', '),
    allowedStoreCategories: (coupon.allowedStoreCategories || []).join(', '),
  });

  const splitCsv = (text) =>
    String(text || '')
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);

  const handleCouponSave = async () => {
    try {
      const allowedPaymentMethods = [];
      if (couponForm.allowRazorpay) allowedPaymentMethods.push('Razorpay');
      if (couponForm.allowCOD) allowedPaymentMethods.push('COD');

      const nthOrderNumber =
        couponForm.nthOrderNumber === '' || couponForm.nthOrderNumber === null
          ? undefined
          : Number(couponForm.nthOrderNumber);

      if (
        nthOrderNumber !== undefined &&
        (!Number.isInteger(nthOrderNumber) || nthOrderNumber < 1)
      ) {
        throw new Error('Nth order must be a whole number greater than 0');
      }

      const payload = {
        code: couponForm.code,
        title: couponForm.title,
        description: couponForm.description,
        discountType: couponForm.discountType,
        discountValue: Number(couponForm.discountValue || 0),
        maxDiscountAmount: couponForm.maxDiscountAmount === '' ? undefined : Number(couponForm.maxDiscountAmount),
        minOrderValue: couponForm.minOrderValue === '' ? undefined : Number(couponForm.minOrderValue),
        validFrom: couponForm.validFrom || undefined,
        validUntil: couponForm.validUntil || undefined,
        usageLimit: couponForm.usageLimit === '' ? undefined : Number(couponForm.usageLimit),
        perUserLimit: couponForm.perUserLimit === '' ? undefined : Number(couponForm.perUserLimit),
        isActive: !!couponForm.isActive,
        firstOrderOnly: !!couponForm.firstOrderOnly,
        nthOrderNumber: couponForm.firstOrderOnly ? undefined : nthOrderNumber,
        excludeSaleItems: !!couponForm.excludeSaleItems,
        allowedPaymentMethods,
        allowedCollections: splitCsv(couponForm.allowedCollections),
        allowedProductTypes: splitCsv(couponForm.allowedProductTypes),
        allowedStoreCategories: splitCsv(couponForm.allowedStoreCategories),
      };

      if (editingCouponId) {
        await updateCoupon(editingCouponId, payload);
        toast.success('Coupon updated');
      } else {
        await createCoupon(payload);
        toast.success('Coupon created');
      }

      resetCouponForm();
      await fetchData();
    } catch (err) {
      showErrorToast(err, 'Failed to save coupon');
    }
  };

  const handleCouponEdit = (coupon) => {
    setCouponForm(mapCouponForForm(coupon));
    setEditingCouponId(coupon._id);
  };

  const handleCouponDelete = (id) => {
    setConfirm({
      open: true,
      title: 'Delete coupon?',
      description: 'This coupon will be removed permanently.',
      onConfirm: async () => {
        try {
          await deleteCoupon(id);
          toast.success('Coupon deleted');
          fetchData();
        } catch (err) {
          showErrorToast(err, 'Failed to delete coupon');
        } finally {
          setConfirm((c) => ({ ...c, open: false }));
        }
      },
      onCancel: () => setConfirm((c) => ({ ...c, open: false })),
    });
  };

  // --- Filter Logic ---
  const sortedData = activeTab === 'orders'
    ? [...data].sort((a, b) => new Date(b.createdAt || b.updatedAt || 0) - new Date(a.createdAt || a.updatedAt || 0))
    : data;

  const filteredData = sortedData.filter(item => {
    const matchesCategory = activeTab !== 'orders' || orderCategoryFilter === 'all' || getOrderCategory(item) === orderCategoryFilter;

    if (!searchQuery) return matchesCategory;

    const q = searchQuery.toLowerCase();
    if (activeTab === 'products') return item.product?.toLowerCase().includes(q) || item.sku?.toLowerCase().includes(q);
    if (activeTab === 'users') return item.name?.toLowerCase().includes(q) || item.email?.toLowerCase().includes(q);
    if (activeTab === 'blogs') return item.title?.toLowerCase().includes(q);
    if (activeTab === 'orders') {
      const orderText = `${item._id || ''} ${item.user?.name || ''} ${item.user?.email || ''} ${item.orderStage || ''}`.toLowerCase();
      return matchesCategory && orderText.includes(q);
    }
    if (activeTab === 'coupons') return item.code?.toLowerCase().includes(q) || item.title?.toLowerCase().includes(q);
    return true;
  });

  const orderCategoryCounts = activeTab === 'orders'
    ? sortedData.reduce((counts, order) => {
        const category = getOrderCategory(order);
        counts.all += 1;
        counts[category] = (counts[category] || 0) + 1;
        return counts;
      }, { all: 0, new: 0, processing: 0, delivered: 0, returned: 0, replaced: 0, cancelled: 0 })
    : null;

  return (
    <div className="flex h-screen font-bdogrotesk text-gray-800">
      
      {/* --- Sidebar --- */}
      <aside className="hidden md:flex flex-col w-72 bg-white border-r border-gray-200 shadow-[2px_0_10px_rgba(0,0,0,0.02)] z-20">
        <div className="flex items-center justify-center h-20 border-b border-gray-100">
          <h1 onClick={()=>navigate('/')} className="cursor-pointer text-2xl text-black font-medium font-bdogrotesk">
            NUFAB ADMIN
          </h1>
        </div>
        <nav className="flex-1 overflow-y-auto py-6 space-y-1">
          <TabButton id="products" label="Products" icon={<Icons.Box />} activeTab={activeTab} setActiveTab={setActiveTab} setSearchQuery={setSearchQuery} />
          <TabButton id="orders" label="Orders" icon={<Icons.ShoppingBag />} activeTab={activeTab} setActiveTab={setActiveTab} setSearchQuery={setSearchQuery} />
          <TabButton id="blogs" label="Blogs" icon={<Icons.FileText />} activeTab={activeTab} setActiveTab={setActiveTab} setSearchQuery={setSearchQuery} />
          <TabButton id="photobooth" label="Photo Booth" icon={<Icons.Upload />} activeTab={activeTab} setActiveTab={setActiveTab} setSearchQuery={setSearchQuery} />
          <TabButton id="coupons" label="Coupons" icon={<Icons.Ticket />} activeTab={activeTab} setActiveTab={setActiveTab} setSearchQuery={setSearchQuery} />
          <TabButton id="users" label="Users" icon={<Icons.Users />} activeTab={activeTab} setActiveTab={setActiveTab} setSearchQuery={setSearchQuery} />
          <TabButton id="settings" label="Settings" icon={<Icons.Settings />} activeTab={activeTab} setActiveTab={setActiveTab} setSearchQuery={setSearchQuery} />
        </nav>
        <div className="p-6 border-t border-gray-100">
            <div className="flex items-center gap-3 mb-6 bg-gray-50 p-3 rounded-xl border border-gray-100">
            <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center text-white font-bold text-lg shadow-md">
              {storedUser?.name?.[0] || 'A'}
            </div>
            <div className="overflow-hidden">
              <p className="text-sm font-bold text-gray-800 truncate">{storedUser?.name || 'Admin'}</p>
              <p className="text-xs text-gray-500 truncate">{storedUser?.email}</p>
            </div>
          </div>
          <button 
            onClick={() => { localStorage.clear(); navigate('/'); }}
            className="w-full flex items-center justify-center space-x-2 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-black rounded-lg transition-colors"
          >
            <Icons.Logout />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* --- Main Content --- */}
      <main className="flex-1 flex flex-col overflow-hidden relative">
        {/* Header */}
        <header className="flex items-center justify-between px-8 py-5 bg-white border-b border-gray-200 z-10">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 capitalize tracking-tight">{activeTab}</h2>
            <p className="text-sm text-gray-500 mt-1">
              {activeTab === 'orders'
                ? 'Flow: Ordered -> Being Made -> Create Shipment -> Shipped -> Delivered. Use Manage for returns, exchanges, and refunds.'
                : `Manage your ${activeTab} data here`}
            </p>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="relative group">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400 group-focus-within:text-indigo-500 transition-colors">
                <Icons.Search />
              </span>
              <input
                type="text"
                placeholder="Search database..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2.5 w-64 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 focus:outline-none transition-all"
              />
            </div>
            {activeTab === 'orders' && (
              <button
                onClick={() => refreshOrdersList()}
                className="flex items-center px-4 py-2.5 bg-white text-gray-800 border border-gray-300 rounded-xl hover:bg-gray-50 shadow-sm transition-all active:scale-95 font-medium"
                title="Refresh orders list"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h5M20 20v-5h-5M5.636 18.364A9 9 0 1020 12" />
                </svg>
                <span>Refresh Orders</span>
              </button>
            )}
            {activeTab === 'orders' && (
              <button
                onClick={async () => {
                  try {
                    toast.loading('Syncing all shipped orders with Delhivery...', { id: 'sync-orders' });
                    const result = await syncAllShippedOrders();
                    toast.success(result.message || 'Orders synced successfully!', { id: 'sync-orders' });
                    if (result.results?.delivered > 0) {
                      toast.success(`🎉 ${result.results.delivered} order(s) auto-marked as delivered!`, { duration: 5000 });
                    }
                    // Refresh orders list after sync
                    await refreshOrdersList({ showSuccessToast: false });
                  } catch (error) {
                    showErrorToast(error, 'Failed to sync orders', { id: 'sync-orders' });
                  }
                }}
                className="flex items-center px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 shadow-lg hover:shadow-lg transition-all active:scale-95 font-medium"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>Sync All Shipped Orders</span>
              </button>
            )}
            {activeTab !== 'orders' && activeTab !== 'settings' && activeTab !== 'coupons' && (
              <button
                onClick={() => handleOpenModal()}
                className="flex items-center px-5 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-black shadow-lg hover:shadow-lg transition-all active:scale-95 font-medium"
              >
                <Icons.Plus />
                <span className="ml-2">Add New</span>
              </button>
            )}
          </div>
        </header>

        {/* Content Table */}
        <div className="flex-1 overflow-auto p-8">
          {loading ? (
            <div className="h-full flex flex-col items-center justify-center text-gray-400">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mb-4"></div>
              Loading NUFAB data...
            </div>
          ) : activeTab === 'settings' ? (
            <SettingsPanel dropdownOptions={dropdownOptions} setDropdownOptions={setDropdownOptions} />
          ) : activeTab === 'coupons' ? (
            <div className="space-y-6">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
                <h3 className="text-lg font-bold text-gray-900 mb-4">{editingCouponId ? 'Edit Coupon' : 'Create Coupon'}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input name="code" value={couponForm.code} onChange={handleCouponFieldChange} placeholder="Code (e.g. WELCOME10)" className="px-3 py-2 border rounded-lg" />
                  <input name="title" value={couponForm.title} onChange={handleCouponFieldChange} placeholder="Title" className="px-3 py-2 border rounded-lg" />
                  <select name="discountType" value={couponForm.discountType} onChange={handleCouponFieldChange} className="px-3 py-2 border rounded-lg bg-white">
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed</option>
                  </select>
                  <input name="discountValue" type="number" min="0" value={couponForm.discountValue} onChange={handleCouponFieldChange} placeholder="Discount value" className="px-3 py-2 border rounded-lg" />
                  <input name="maxDiscountAmount" type="number" min="0" value={couponForm.maxDiscountAmount} onChange={handleCouponFieldChange} placeholder="Max discount amount (optional)" className="px-3 py-2 border rounded-lg" />
                  <input name="minOrderValue" type="number" min="0" value={couponForm.minOrderValue} onChange={handleCouponFieldChange} placeholder="Min order value (optional)" className="px-3 py-2 border rounded-lg" />
                  <input name="usageLimit" type="number" min="0" value={couponForm.usageLimit} onChange={handleCouponFieldChange} placeholder="Total usage limit (optional)" className="px-3 py-2 border rounded-lg" />
                  <input name="perUserLimit" type="number" min="0" value={couponForm.perUserLimit} onChange={handleCouponFieldChange} placeholder="Per-user limit (optional)" className="px-3 py-2 border rounded-lg" />
                  <input name="nthOrderNumber" type="number" min="1" step="1" value={couponForm.nthOrderNumber} onChange={handleCouponFieldChange} placeholder="Nth order only (optional, e.g. 2)" className="px-3 py-2 border rounded-lg" />
                  <input name="validFrom" type="datetime-local" value={couponForm.validFrom} onChange={handleCouponFieldChange} className="px-3 py-2 border rounded-lg" />
                  <input name="validUntil" type="datetime-local" value={couponForm.validUntil} onChange={handleCouponFieldChange} className="px-3 py-2 border rounded-lg" />
                  <input name="allowedCollections" value={couponForm.allowedCollections} onChange={handleCouponFieldChange} placeholder="Allowed collections (comma separated)" className="px-3 py-2 border rounded-lg" />
                  <input name="allowedProductTypes" value={couponForm.allowedProductTypes} onChange={handleCouponFieldChange} placeholder="Allowed product types (comma separated)" className="px-3 py-2 border rounded-lg" />
                  <input name="allowedStoreCategories" value={couponForm.allowedStoreCategories} onChange={handleCouponFieldChange} placeholder="Allowed store categories (comma separated)" className="px-3 py-2 border rounded-lg" />
                  <textarea name="description" value={couponForm.description} onChange={handleCouponFieldChange} placeholder="Description" className="px-3 py-2 border rounded-lg md:col-span-2" rows="3" />
                </div>

                <div className="flex flex-wrap gap-5 mt-4">
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" name="isActive" checked={couponForm.isActive} onChange={handleCouponFieldChange} /> Active</label>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" name="firstOrderOnly" checked={couponForm.firstOrderOnly} onChange={handleCouponFieldChange} /> First order only</label>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" name="excludeSaleItems" checked={couponForm.excludeSaleItems} onChange={handleCouponFieldChange} /> Exclude sale items</label>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" name="allowRazorpay" checked={couponForm.allowRazorpay} onChange={handleCouponFieldChange} /> Razorpay</label>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" name="allowCOD" checked={couponForm.allowCOD} onChange={handleCouponFieldChange} /> COD</label>
                </div>

                <div className="flex gap-3 mt-5">
                  <button type="button" onClick={handleCouponSave} className="px-4 py-2 rounded-lg bg-black text-white text-sm font-semibold">
                    {editingCouponId ? 'Update Coupon' : 'Create Coupon'}
                  </button>
                  {editingCouponId && (
                    <button type="button" onClick={resetCouponForm} className="px-4 py-2 rounded-lg bg-gray-100 text-sm font-semibold">
                      Cancel Edit
                    </button>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                <table className="min-w-full divide-y divide-gray-100">
                  <thead className="bg-gray-50/50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Code</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Discount</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Usage</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {filteredData.map((item) => (
                      <tr key={item._id} className="hover:bg-gray-50/80 transition-colors">
                        <td className="px-6 py-4">
                          <div className="text-sm font-bold text-gray-900">{item.code}</div>
                          <div className="text-xs text-gray-500">{item.title}</div>
                          <div className="text-xs text-gray-500">
                            {item.firstOrderOnly
                              ? 'First order only'
                              : item.nthOrderNumber
                                ? `Order #${item.nthOrderNumber} only`
                                : 'All eligible orders'}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {item.discountType === 'percentage' ? `${item.discountValue}%` : `₹${item.discountValue}`}
                          {item.maxDiscountAmount ? ` (max ₹${item.maxDiscountAmount})` : ''}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {item.usedCount || 0}/{item.usageLimit || '∞'}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <span className={`px-2.5 py-1 inline-flex text-xs leading-5 font-bold rounded-full border ${item.isActive ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                            {item.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right space-x-2">
                          <button onClick={() => handleCouponEdit(item)} className="text-indigo-600 bg-white hover:bg-indigo-200 p-2 rounded-lg transition-colors" title="Edit">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                          </button>
                          <button onClick={() => handleCouponDelete(item._id)} className="text-red-600 bg-white hover:bg-red-100 p-2 rounded-lg transition-colors" title="Delete">
                            <Icons.Trash />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {filteredData.length === 0 && (
                      <tr><td colSpan="5" className="px-6 py-12 text-center text-gray-400">No coupons found.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {activeTab === 'orders' && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4">
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(ORDER_CATEGORY_META).map(([key, meta]) => {
                      const count = orderCategoryCounts?.[key] ?? 0;
                      const active = orderCategoryFilter === key;
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setOrderCategoryFilter(key)}
                          className={`px-3 py-2 rounded-xl border text-sm font-semibold transition-colors ${active ? 'bg-black text-white border-black' : `${meta.badge} bg-white hover:bg-gray-50`}`}
                        >
                          <span>{meta.label}</span>
                          <span className={`ml-2 text-xs font-bold ${active ? 'text-white/80' : 'text-gray-500'}`}>
                            {count}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-gray-50/50">
                  <tr>
                    {activeTab === 'products' && (
                      <React.Fragment>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Product</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Details</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Inventory</th>
                        <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                      </React.Fragment>
                    )}
                    {activeTab === 'photobooth' && (
                      <React.Fragment>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Preview</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Garment</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Product</th>
                        <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                      </React.Fragment>
                    )}
                    {activeTab === 'users' && (
                       <React.Fragment>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">User</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Email</th>
                        <th className="px-6 py-4 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">Role Access</th>
                        <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                      </React.Fragment>
                    )}
                    {activeTab === 'blogs' && (
                      <React.Fragment>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Article</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Slug</th>
                        <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                      </React.Fragment>
                    )}
                    {activeTab === 'orders' && (
                      <React.Fragment>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Order ID</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Customer</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Category</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Placed</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Total</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                      </React.Fragment>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {filteredData.length === 0 && (
                    <tr><td colSpan={activeTab === 'orders' ? 7 : 4} className="px-6 py-12 text-center text-gray-400">No records found matching your search.</td></tr>
                  )}
                  
                  {/* Products with Drag and Drop */}
                  {activeTab === 'products' && filteredData.length > 0 && (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleProductDragEnd}
                    >
                      <SortableContext items={filteredData.map(item => item._id)} strategy={rectSortingStrategy}>
                        {filteredData.map((item) => (
                          <SortableProductRow
                            key={item._id}
                            item={item}
                            handleOpenModal={handleOpenModal}
                            handleDelete={handleDelete}
                          />
                        ))}
                      </SortableContext>
                    </DndContext>
                  )}

                  {activeTab === 'blogs' && filteredData.length > 0 && (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleBlogDragEnd}
                    >
                      <SortableContext items={filteredData.map(item => item._id)} strategy={rectSortingStrategy}>
                        {filteredData.map((item) => (
                          <SortableBlogRow
                            key={item._id}
                            item={item}
                            handleOpenModal={handleOpenModal}
                            handleDelete={handleDelete}
                          />
                        ))}
                      </SortableContext>
                    </DndContext>
                  )}

                  {activeTab === 'photobooth' && filteredData.length > 0 && (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handlePhotoboothDragEnd}
                    >
                      <SortableContext items={filteredData.map(item => item._id)} strategy={rectSortingStrategy}>
                        {filteredData.map((item) => (
                          <SortablePhotoboothRow
                            key={item._id}
                            item={item}
                            handleOpenModal={handleOpenModal}
                            handleDelete={handleDelete}
                          />
                        ))}
                      </SortableContext>
                    </DndContext>
                  )}

                  {/* Other tabs without drag and drop */}
                  {activeTab !== 'products' && activeTab !== 'blogs' && activeTab !== 'photobooth' && filteredData.map((item) => (
                    <tr key={item._id} className="hover:bg-gray-50/80 transition-colors group">

                      {/* --- User Rows --- */}
                      {activeTab === 'users' && (
                        <React.Fragment>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-bold text-gray-900">{item.name}</div>
                            <div className="text-xs text-gray-400">ID: {item._id.slice(-6)}</div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600 font-mono">{item.email}</td>
                          <td className="px-6 py-4 flex justify-center">
                            {/* Custom Switch Toggle for Role */}
                            <button 
                              onClick={() => handleUserRoleToggle(item)}
                              title={item.role === 'admin' ? "Click to Demote" : "Click to Promote"}
                              className={`relative inline-flex items-center h-7 w-12 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${item.role === 'admin' ? 'bg-indigo-600' : 'bg-gray-300'}`}
                            >
                              <span className={`inline-block w-5 h-5 transform bg-white rounded-full transition-transform duration-200 shadow-sm ${item.role === 'admin' ? 'translate-x-6' : 'translate-x-1'}`} />
                            </button>
                            <span className={`ml-3 text-xs font-bold uppercase py-1 px-2 rounded-md self-center ${item.role === 'admin' ? 'text-indigo-700 bg-indigo-50' : 'text-gray-500 bg-gray-100'}`}>
                              {item.role}
                            </span>
                          </td>
                        </React.Fragment>
                      )}

                      {/* --- Blog Rows --- */}
                      {activeTab === 'blogs' && (
                          <React.Fragment>
                          <td className="px-6 py-4">
                             <div className="flex items-center gap-3">
                                {item.images && item.images[0] && (
                                  <img src={getOptimizedUrl(item.images[0] || '', 200)} alt="" className="w-10 h-10 rounded object-cover border border-gray-200" />
                                )}
                                <div>
                                  <div className="text-sm font-medium text-gray-900">{item.title}</div>
                                   <div className="text-xs text-gray-400 line-clamp-1">{item.previewSnippet || item.intro || item.excerpt}</div>
                                </div>
                             </div>
                          </td>
                          <td className="px-6 py-4 text-xs font-mono text-gray-500 bg-gray-50 rounded p-1 w-fit">{item.slug}</td>
                        </React.Fragment>
                      )}

                      {/* --- PhotoBooth Rows --- */}
                      {activeTab === 'photobooth' && (
                        <React.Fragment>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              {item.imageUrl ? (
                                <img src={getOptimizedUrl(item.imageUrl || '', 200)} alt="" className="w-12 h-12 rounded object-cover border border-gray-200" />
                              ) : (
                                <div className="w-12 h-12 bg-gray-50 rounded flex items-center justify-center text-gray-300">No</div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900">{item.garmentName || '—'}</div>
                            <div className="text-xs text-gray-400">{item.alt || ''}</div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700">{item.product?.product || '-'}</td>
                        </React.Fragment>
                      )}

                      {/* --- Order Rows --- */}
                       {activeTab === 'orders' && (
                          <React.Fragment>
                          <td className="px-6 py-4 text-sm font-mono text-gray-600">{item._id.substring(0,10)}...</td>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">{item.user?.name || 'Guest'}</td>
                          <td className="px-6 py-4 text-sm">
                            <span className={`px-2.5 py-1 inline-flex text-xs leading-5 font-bold rounded-full border ${ORDER_CATEGORY_META[getOrderCategory(item)]?.badge || 'bg-gray-50 text-gray-700 border-gray-200'}`}>
                              {ORDER_CATEGORY_META[getOrderCategory(item)]?.label || 'New'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {item.createdAt ? new Date(item.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                          </td>
                          <td className="px-6 py-4 text-sm font-bold text-gray-900">₹{item.totalPrice}</td>
                          <td className="px-6 py-4">
                            <span className={`px-2.5 py-1 inline-flex text-xs leading-5 font-bold rounded-full border
                              ${getOrderStageMeta(item.orderStage).badge}`}>
                              {getOrderStatusLabel(item)}
                            </span>
                            {getOrderActivitySummary(item).labels.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1.5 max-w-[14rem]">
                                {getOrderActivitySummary(item).labels.map((label) => (
                                  <span key={`${item._id}-${label}`} className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[10px] font-semibold text-gray-600">
                                    {label}
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleOpenModal(item)}
                              className="text-indigo-600 hover:text-indigo-600 bg-indigo-50 hover:bg-indigo-100 p-2 rounded-lg transition-colors"
                              title="Manage"
                            >
                              Manage
                            </button>
                            {shouldShowRefundInitiate(item) ? (
                              <button
                                onClick={() => handleOpenModal(item, 'payment')}
                                className="px-3 py-1.5 rounded-lg border border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100 text-xs font-semibold transition-colors"
                                title="Open payment details to initiate refund"
                              >
                                Refund Initiate
                              </button>
                            ) : item.orderStage === 'ordered' ? (
                              <button
                                onClick={() => handleChangeOrderStatus(item._id, 'being_made')}
                                className="px-3 py-1.5 rounded-lg border border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100 text-xs font-semibold transition-colors"
                                title="Accept order and move to being made"
                              >
                                Accept
                              </button>
                            ) : null}
                            {item.orderStage === 'being_made' && (
                              <span className="px-3 py-1.5 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 text-xs font-semibold">
                                Ready for shipment
                              </span>
                            )}
                            </td>
                          </React.Fragment>
                        )}

                      {/* --- Common Actions --- */}
                      {activeTab !== 'orders' && (
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button 
                            onClick={() => handleOpenModal(item)} 
                            className="text-indigo-600 bg-white hover:bg-indigo-200 p-2 rounded-lg mr-2 transition-colors"
                            title="Edit"
                          >
                             <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                          </button>
                          <button 
                            onClick={() => handleDelete(item._id)} 
                            className="text-red-600 bg-white hover:bg-red-100 p-2 rounded-lg transition-colors"
                            title="Delete"
                          >
                             <Icons.Trash />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </div>
          )}
        </div>
      </main>

      {/* --- CENTERED MODAL --- */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" role="dialog" aria-modal="true">
          {/* Backdrop Blur Overlay */}
          <div 
            className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity" 
            onClick={() => setIsModalOpen(false)}
          ></div>

          {/* Modal Panel */}
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col transform transition-all animate-fade-in-up">
            
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50">
               <div>
                 <h3 className="text-xl font-bold text-gray-900">
                   {modalMode === 'create' ? 'Create New' : 'Edit'} <span className="capitalize">{activeTab.slice(0, -1)}</span>
                 </h3>
                 <p className="text-sm text-gray-500">Fill in the details below.</p>
               </div>
               <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600 bg-white p-1 rounded-full shadow-sm border border-gray-200 transition-colors">
                 <Icons.X />
               </button>
            </div>

            {/* Modal Body (Scrollable) */}
            <div className="flex-1 overflow-y-auto p-6 bg-white">
              <form id="admin-form" onSubmit={handleSave}>
                
                {/* --- Product Form (Tabbed) --- */}
                {activeTab === 'products' && (
                  <div>
                    <div className="flex space-x-1 bg-gray-100 p-1 rounded-xl mb-6 w-fit">
                       {['basic', 'fabric', 'pricing', 'media', 'seo'].map(tab => (
                         <button
                           type="button"
                           key={tab}
                           onClick={() => setProductFormTab(tab)}
                           className={`py-1.5 px-4 text-sm font-semibold rounded-lg capitalize transition-all ${
                             productFormTab === tab
                             ? 'bg-white text-indigo-600 shadow-sm'
                             : 'text-gray-500 hover:text-gray-700'
                           }`}
                         >
                           {tab}
                         </button>
                       ))}
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                      {productFormTab === 'basic' && (
                        <React.Fragment>
                          <InputGroup label="Product Name" name="product" formData={formData} onChange={handleInputChange} />
                          <SelectGroup 
                            label="Collection" 
                            name="category"
                            options={dropdownOptions.collection}
                            formData={formData} 
                            onChange={handleInputChange} 
                          />
                          <InputGroup label="Slug (URL)" name="slug" formData={formData} onChange={handleInputChange} />
                          <InputGroup label="Variant Group" name="variantGroup" formData={formData} onChange={handleInputChange} />
                          <SelectGroup 
                            label="Product Type" 
                            name="productType" 
                            options={dropdownOptions.productType}
                            formData={formData} 
                            onChange={handleInputChange} 
                          />
                          <SelectGroup
                            label="Store Category"
                            name="storeCategory"
                            options={dropdownOptions.storeCategories || []}
                            formData={formData}
                            onChange={handleInputChange}
                          />
                          <InputGroup label="Description" name="productDescription" type="textarea" colSpan={2} formData={formData} onChange={handleInputChange} />
                          <InputGroup label="Show on Homepage" name="isShownInHomepage" type="checkbox" colSpan={2} formData={formData} onChange={handleInputChange} />
                          <InputGroup label="Show on Fanout" name="isShownInFanout" type="checkbox" colSpan={2} formData={formData} onChange={handleInputChange} />
                          <div className="col-span-2 md:col-span-1">
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Fanout Alignment</label>
                            <select
                              name="fanoutPosition"
                              value={formData?.fanoutPosition || ''}
                              onChange={handleInputChange}
                              disabled={!formData?.isShownInFanout}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-shadow bg-white cursor-pointer disabled:bg-gray-100 disabled:text-gray-400"
                            >
                              <option value="">Select alignment</option>
                              <option value="left">Left</option>
                              <option value="center">Center</option>
                              <option value="right">Right</option>
                            </select>
                            <p className="text-xs text-gray-500 mt-1">Only 3 products are allowed in fanout, one per alignment slot.</p>
                          </div>
                        </React.Fragment>
                      )}
                      
                      {productFormTab === 'fabric' && (
                          <React.Fragment>
                          <SelectGroup 
                            label="Fabric Print" 
                            name="print" 
                            options={dropdownOptions.fabricPrint}
                            formData={formData} 
                            onChange={handleInputChange} 
                          />
                          <SelectGroup 
                            label="Color" 
                            name="color" 
                            options={dropdownOptions.color}
                            formData={formData} 
                            onChange={handleInputChange} 
                          />
                          <SelectGroup 
                            label="Fabric Type" 
                            name="fabricType" 
                            options={dropdownOptions.fabricType}
                            formData={formData} 
                            onChange={handleInputChange} 
                          />
                          <SelectGroup 
                            label="Fabric Pattern" 
                            name="fabricPattern" 
                            options={dropdownOptions.fabricPattern}
                            formData={formData} 
                            onChange={handleInputChange} 
                          />
                          <InputGroup label="Material Composition" name="material" formData={formData} onChange={handleInputChange} />
                          <SelectGroup 
                            label="Sleeves Style" 
                            name="sleeves" 
                            options={dropdownOptions.sleeves}
                            formData={formData} 
                            onChange={handleInputChange} 
                          />
                          <SelectGroup 
                            label="Closure Type" 
                            name="closure" 
                            options={dropdownOptions.closure}
                            formData={formData} 
                            onChange={handleInputChange} 
                          />
                          <div className="grid grid-cols-2 gap-2">
                             <SelectGroup 
                               label="Length" 
                               name="length" 
                               options={dropdownOptions.length}
                               formData={formData} 
                               onChange={handleInputChange} 
                             />
                             <InputGroup label="Length (Inch)" name="lengthInInches" type="number" formData={formData} onChange={handleInputChange} />
                          </div>
                          <InputGroup label="Features (comma separated)" name="features" colSpan={2} placeholder="e.g. Breathable, Lightweight, Cotton" formData={formData} onChange={handleInputChange} />
                          <InputGroup label="Size & Fit Info" name="sizeAndFit" type="textarea" colSpan={2} formData={formData} onChange={handleInputChange} />
                        </React.Fragment>
                      )}
                      {productFormTab === 'pricing' && (
                        <React.Fragment>
                          <div className="bg-blue-50 p-4 rounded-xl col-span-2 grid grid-cols-2 gap-4 border border-blue-100">
                             <InputGroup label="MRP (₹)" name="mrp" type="number" formData={formData} onChange={handleInputChange} />
                             <InputGroup label="Selling Price (₹)" name="discountedPrice" type="number" formData={formData} onChange={handleInputChange} />
                          </div>
                          <div className="col-span-2"><h4 className="font-bold text-gray-800">Inventory Management</h4></div>
                          {isAccessory(formData.productType) ? (
                            <>
                              <InputGroup label="Freesize Qty" name="freesizeQuantity" type="number" formData={formData} onChange={handleInputChange} />
                              <div className="col-span-2 text-xs text-gray-400 bg-gray-50 p-2 rounded">
                                This accessory uses freesize inventory.
                              </div>
                            </>
                          ) : (
                            <>
                              <InputGroup label="XXS Qty" name="sizeXXSQuantity" type="number" formData={formData} onChange={handleInputChange} />
                              <InputGroup label="XS Qty" name="sizeXSQuantity" type="number" formData={formData} onChange={handleInputChange} />
                              <InputGroup label="Small Qty" name="sizeSQuantity" type="number" formData={formData} onChange={handleInputChange} />
                              <InputGroup label="Medium Qty" name="sizeMQuantity" type="number" formData={formData} onChange={handleInputChange} />
                              <InputGroup label="Large Qty" name="sizeLQuantity" type="number" formData={formData} onChange={handleInputChange} />
                              <InputGroup label="XL Qty" name="sizeXLQuantity" type="number" formData={formData} onChange={handleInputChange} />
                              <InputGroup label="XXL Qty" name="sizeXXLQuantity" type="number" formData={formData} onChange={handleInputChange} />
                              <div className="col-span-2 text-xs text-gray-400 bg-gray-50 p-2 rounded">
                                Total Quantity will be calculated automatically based on sizes.
                              </div>
                            </>
                          )}
                        </React.Fragment>
                      )}
                      {productFormTab === 'media' && (
                        <ImageUploadSection
                          images={formData.images}
                          fileMap={fileMap}
                          setFileMap={setFileMap}
                          onDeleteImage={handleDeleteImage}
                          onReorderImages={handleReorderImages}
                        />
                      )}
                      {productFormTab === 'seo' && (
                        <React.Fragment>
                          <div className="col-span-2 bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <h3 className="text-sm font-bold text-gray-700 mb-2">SEO Settings</h3>
                            <p className="text-xs text-gray-500 mb-4">These fields are hidden from users and help improve search engine visibility.</p>
                          </div>
                          <InputGroup
                            label="Meta Title"
                            name="metaTitle"
                            placeholder="SEO-optimized title for search engines (50-60 characters recommended)"
                            colSpan={2}
                            formData={formData}
                            onChange={handleInputChange}
                          />
                          <InputGroup
                            label="Meta Description"
                            name="metaDescription"
                            type="textarea"
                            rows={3}
                            placeholder="Brief description for search results (150-160 characters recommended)"
                            colSpan={2}
                            formData={formData}
                            onChange={handleInputChange}
                          />
                          <InputGroup
                            label="SEO Keywords"
                            name="seoKeywords"
                            type="textarea"
                            rows={2}
                            placeholder="Comma-separated keywords (e.g., kurti, ethnic wear, cotton dress, traditional clothing)"
                            colSpan={2}
                            formData={formData}
                            onChange={handleInputChange}
                          />
                          <div className="col-span-2 bg-blue-50 p-3 rounded-lg border border-blue-200">
                            <p className="text-xs text-blue-700">
                              <strong>Tip:</strong> Use relevant keywords naturally. Focus on what customers might search for when looking for this product.
                            </p>
                          </div>
                        </React.Fragment>
                      )}
                    </div>
                  </div>
                )}

                {/* --- Photo Booth Form --- */}
                {activeTab === 'photobooth' && (
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Preview</label>
                      {fileMap ? (
                        <img src={URL.createObjectURL(fileMap)} alt="preview" className="w-full h-48 object-cover rounded-lg border border-gray-200" />
                      ) : formData.imageUrl ? (
                        <img src={getOptimizedUrl(formData.imageUrl || '', 1200)} alt="preview" className="w-full h-48 object-cover rounded-lg border border-gray-200" />
                      ) : (
                        <div className="w-full h-48 bg-gray-50 rounded-lg border border-dashed border-gray-300 flex items-center justify-center text-gray-400">No image</div>
                      )}
                      <div className="mt-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Upload Image</label>
                        <input type="file" accept="image/*" onChange={(e) => setFileMap(e.target.files[0])} />
                        <p className="text-xs text-gray-500 mt-2">PNG/JPG up to 5MB. When creating, select an image; edits change metadata only.</p>
                      </div>
                    </div>

                    <div>
                      <InputGroup label="Garment Name" name="garmentName" formData={formData} onChange={handleInputChange} />
                      <InputGroup label="Model Name" name="modelName" formData={formData} onChange={handleInputChange} />
                      <div className="mt-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Link To Product</label>
                        <select name="product" value={formData.product || ''} onChange={handleInputChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white">
                          <option value="">(no product)</option>
                          {productsList.map(p => (
                            <option key={p._id} value={p._id}>{p.product}</option>
                          ))}
                        </select>
                      </div>
                      <InputGroup label="Alt Text" name="alt" formData={formData} onChange={handleInputChange} />
                    </div>
                  </div>
                )}

                {/* --- Blog Form (Now with Image Support) --- */}
                {activeTab === 'blogs' && (
                  <div className="grid grid-cols-1 gap-6">
                      <div className="grid grid-cols-2 gap-4">
                        <InputGroup label="Article Title" name="title" formData={formData} onChange={handleInputChange} />
                        <InputGroup label="Subtitle" name="subtitle" formData={formData} onChange={handleInputChange} />
                        <InputGroup label="URL Slug" name="slug" formData={formData} onChange={handleInputChange} />
                          <InputGroup label="Reading Time (min)" name="readingTime" type="number" formData={formData} onChange={handleInputChange} />
                      </div>
                      <InputGroup label="Preview Snippet (Home + Blog Card)" name="previewSnippet" type="textarea" placeholder="Enter 1-2 manual lines for preview..." formData={formData} onChange={handleInputChange} />
                      <InputGroup label="Legacy Excerpt (Optional)" name="intro" type="textarea" placeholder="Optional old excerpt field..." formData={formData} onChange={handleInputChange} />
                      
                      {/* Added Image Section for Blogs */}
                      <div className="border rounded-xl p-4 bg-gray-50">
                        <h4 className="text-sm font-bold text-gray-700 mb-3">Cover Image</h4>
                        <ImageUploadSection 
                          images={formData.images} 
                          fileMap={fileMap} 
                          setFileMap={setFileMap} 
                          onDeleteImage={handleDeleteImage} 
                          onReorderImages={handleReorderImages}
                        />
                      </div>

                      <div className="col-span-2">
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Main Content</label>
                        <div className="space-y-3">
                          {(Array.isArray(formData.content) ? formData.content : []).map((block, idx) => (
                            <div key={idx} className="relative">
                              <textarea
                                value={block}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setFormData(prev => {
                                    const next = { ...prev };
                                    next.content = Array.isArray(next.content) ? [...next.content] : [];
                                    next.content[idx] = v;
                                    return next;
                                  });
                                }}
                                rows={4}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-shadow"
                                placeholder={`Paragraph ${idx + 1}`}
                              />
                              <button type="button" onClick={() => {
                                setFormData(prev => {
                                  const next = { ...prev };
                                  if (!Array.isArray(next.content)) next.content = [];
                                  next.content = next.content.filter((_, i) => i !== idx);
                                  return next;
                                });
                              }} className="absolute right-2 top-2 text-red-600 bg-white p-1 rounded hover:bg-red-50">Remove</button>
                            </div>
                          ))}

                          <div>
                            <button type="button" onClick={() => setFormData(prev => ({ ...prev, content: Array.isArray(prev.content) ? [...prev.content, ''] : [''] }))}
                              className="px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors">
                              Add Block
                            </button>
                            <p className="text-xs text-gray-400 mt-2">Each block is stored as one entry in the blog content array. Use the editor to add multiple paragraphs or sections.</p>
                          </div>
                        </div>
                      </div>
                  </div>
                )}

                {/* --- Order Form (View Details) --- */}
                {activeTab === 'orders' && (
                  <div className="space-y-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="text-sm text-gray-500">Order ID</div>
                        <div className="font-mono text-sm text-gray-900">{formData._id}</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Order Stage</div>
                        <select
                          value={formData.orderStage || 'ordered'}
                          onChange={(e) => setFormData(f => ({ ...f, orderStage: e.target.value }))}
                          className="px-3 py-1 rounded-md border"
                        >
                          <option value="ordered">ordered</option>
                          <option value="being_made">being_made</option>
                          <option value="shipped">shipped</option>
                          <option value="delivered">delivered</option>
                          <option value="cancelled">cancelled</option>
                          <option value="return_requested">return_requested</option>
                          <option value="return_approved">return_approved</option>
                          <option value="return_processing">return_processing</option>
                          <option value="return_received_to_store">return_received_to_store</option>
                          <option value="refund_initiated">refund_initiated</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-bold">Customer</h4>
                      <div className="text-sm text-gray-700">{formData.user?.name} <span className="text-xs text-gray-500">{formData.user?.email}</span></div>
                    </div>

                    <div>
                      <h4 className="font-bold">Shipping Address</h4>
                      <div className="text-sm text-gray-700">
                        {formData.shippingAddress ? (
                          <div>
                            <div>{formData.shippingAddress.name} {formData.shippingAddress.label ? `(${formData.shippingAddress.label})` : ''}</div>
                            <div>{formData.shippingAddress.line1}{formData.shippingAddress.line2 ? `, ${formData.shippingAddress.line2}` : ''}</div>
                            <div>{formData.shippingAddress.city}, {formData.shippingAddress.state} — {formData.shippingAddress.postalCode}</div>
                            <div>{formData.shippingAddress.country}</div>
                            <div className="text-xs text-gray-500">Phone: {formData.shippingAddress.phone}</div>
                          </div>
                        ) : <div className="text-gray-500">No shipping address</div>}
                      </div>
                    </div>

                    <div>
                      <h4 className="font-bold">Items</h4>
                      <div className="space-y-2">
                        {Array.isArray(formData.items) && formData.items.map((it, idx) => (
                          <div key={idx} className="flex justify-between items-center text-sm">
                            <div className="flex items-center gap-3">
                              {it.image && <img src={it.image} alt="" className="w-10 h-10 object-cover rounded" />}
                              <div>
                                <div className="font-medium">{it.productName || it.product}</div>
                                <div className="text-xs text-gray-500">{it.color} • {it.size}</div>
                              </div>
                            </div>
                            <div className="text-sm">Qty: {it.quantity} • ₹{it.price}</div>
                          </div>
                        ))}
                        {(!Array.isArray(formData.items) || formData.items.length === 0) && (
                          <div className="text-gray-500">No items in this order.</div>
                        )}
                      </div>
                    </div>

                    <div className="flex justify-between items-center pt-3 border-t">
                      <div className="text-sm text-gray-500">Payment</div>
                      <div className="font-bold">₹{formData.totalPrice} • {formData.paymentMethod}</div>
                    </div>
                  </div>
                )}
              </form>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="px-5 py-2.5 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-100 focus:outline-none transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="admin-form"
                disabled={loading}
                className="px-6 py-2.5 rounded-xl bg-gray-900 text-white font-medium hover:bg-black shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>

          </div>
        </div>
      )}

      {/* --- ORDER DETAILS MODAL --- */}
      {isOrderDetailsOpen && selectedOrderId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" role="dialog" aria-modal="true">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity" 
            onClick={() => {
              setIsOrderDetailsOpen(false);
              setSelectedOrderId(null);
            }}
          ></div>

          {/* Modal Panel */}
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col transform transition-all animate-fade-in-up">
            <AdminOrderDetails 
              orderId={selectedOrderId} 
              initialTab={selectedOrderInitialTab}
              onClose={() => {
                setIsOrderDetailsOpen(false);
                setSelectedOrderId(null);
                setSelectedOrderInitialTab('overview');
                fetchData(); // Refresh orders list after closing
              }} 
            />
          </div>
        </div>
      )}

      <ConfirmModal open={confirm.open} title={confirm.title} description={confirm.description} onConfirm={confirm.onConfirm} onCancel={confirm.onCancel} />
    </div>
  );
}